import React from 'react';
import PlanningDetailsItem from '../Domain/PlanningDetailsItem.js';
import PlanningDetailsForReportingItem from '../Domain/PlanningDetailsForReportingItem.js';
import dateTimeHelper from '../Helpers/datetimeHelper.js';
import mathHelper from '../Helpers/mathHelper.js';
import PlanningDetailHelper from '../Helpers/ModelHelpers/PlanningDetailHelper.js';
import AuthService from './authService.js';

class PlanningDetailsItemService extends React.Component {
  static async GetPlanningDetailsItemsByPricingId(
    orderBy,
    orderDir,
    pageNumber,
    numberOfElements,
    searchItem,
    clearCache = false
  ) {
    const pricingId = mathHelper.ForceConvertToNumber(searchItem.PricingId);
    const url =
      `api/VPlanningDetailsBase/GetvPlanningDetailsBaseByPricingId?` +
      `id=${pricingId}&orderby=${orderBy}&orderDirection=${orderDir}` +
      `&pageNumber=${pageNumber}&numberOfElements=${numberOfElements}&clearCache=${clearCache}`;
    const items = await AuthService.getInstance().CallBackendFunction(url);
    await PlanningDetailsItemService.MergeRolloverSummary(items, pricingId);
    return items;
  }

  // Merges per-period rolled-in/out volumes onto the raw list items (before conversion),
  // so the planning list can show inline +/- rollover badges. A 403 (no rollover role)
  // or any error is swallowed — the list simply renders without badges.
  static async MergeRolloverSummary(items, pricingId) {
    if (!items || !items.length) return;
    try {
      const summary = await PlanningDetailsItemService.GetRolloverSummaryByPricingId(pricingId);
      const map = {};
      (summary || []).forEach((s) => {
        map[s.planningDetailId] = s;
      });
      items.forEach((it) => {
        const s = map[it.planningDetailId];
        if (s) {
          it.rolledInVolume = s.rolledInVolume;
          it.rolledOutVolume = s.rolledOutVolume;
        }
      });
    } catch (e) {
      // No rollover-summary access (e.g. 403) → list shown without badges.
    }
  }

  static async GetPlanningDetailsDatesByPricingIds(pricingIds) {
    const pricingIdsJson = JSON.stringify(pricingIds);
    return AuthService.getInstance().CallBackendFunctionPost(
      'api/VPlanningDetailsBase/GetPlanningDetailsDatesByPricingIds',
      pricingIdsJson
    );
  }

  static async GetPlanningDetailsItemsWithFilter(filter, orderBy, orderDirection = 'Asc') {
    filter.StartDate = dateTimeHelper.startOfTheDay(filter.StartDate);
    filter.EndDate = dateTimeHelper.endOfTheDay(filter.EndDate);
    orderBy = orderBy === 'EndDate' ? '' : orderBy;

    const filterJSON = JSON.stringify(filter);
    const url = `api/VPlanningDetailsBase/GetvPlanningDetailsBaseWithFilter?filter=${filterJSON}&orderby=${orderBy}&orderDirection=${orderDirection}`;
    return AuthService.getInstance().CallBackendFunction(url);
  }

  static async GetPlanningDetailsItemsReport(filter, orderBy, orderDirection = 'Asc') {
    filter.StartDate = dateTimeHelper.toYYYYsMMsDD(filter.StartDate);
    filter.EndDate = dateTimeHelper.toYYYYsMMsDD(filter.EndDate);
    orderBy = orderBy === 'EndDate' ? '' : orderBy;

    const filterJSON = JSON.stringify(filter);
    const url = `api/VPlanningDetailsBase/GetvPlanningDetailsBaseReport?filter=${filterJSON}&orderby=${orderBy}&orderDirection=${orderDirection}`;
    return AuthService.getInstance().CallBackendFunction(url);
  }

  static formatDateArray(dateArray) {
    for (let i = 0; i < dateArray.length; i++) {
      dateArray[i] = dateTimeHelper.toYYYYsMMsDD(dateArray[i]);
    }
  }

  static EditMultiplePlanningDetailItems(planningDetailIds, data) {
    const idsJSON = JSON.stringify(planningDetailIds);
    const targetAssignmentRateJSON = JSON.stringify(data.targetAssignmentRate);
    const targetCompletionRateJSON = JSON.stringify(data.targetCompletionRate);

    const url =
      'api/VPlanningDetailsBase/EditMultipleVPlanningDetailsBaseItems/' +
      '?ids=' +
      idsJSON +
      '&numberOfPlannedAssignments=' +
      data.numberOfPlannedAssignments +
      '&applyTargetRate=' +
      data.applyValue.toString() +
      '&targetAssignmentRate=' +
      targetAssignmentRateJSON +
      '&targetCompletionRate=' +
      targetCompletionRateJSON;
    return AuthService.getInstance().CallBackendFunction(url);
  }

  static MassStatusUpdate(planningDetailIds, to, cancellationDecision, cancellationComment) {
    const data = {
      Ids: JSON.stringify(planningDetailIds),
      NewStatus: to,
      CancellationDecision: cancellationDecision ?? null,
      CancellationComment: cancellationComment ?? null,
    };
    return AuthService.getInstance().CallBackendFunctionPut('api/VPlanningDetailsBase/MassStatusUpdate/', data);
  }

  static EditMultiplePlanningDetailItemsByPricingIds(
    pricingIds,
    applyDateRow,
    newStartDates,
    newEndDates,
    subpanelValues
  ) {
    PlanningDetailsItemService.formatDateArray(newStartDates);
    PlanningDetailsItemService.formatDateArray(newEndDates);

    const idsJSON = JSON.stringify(pricingIds);
    const applyDateRowJSON = JSON.stringify(applyDateRow);
    const newStartDatesJSON = JSON.stringify(newStartDates);
    const newEndDatesJSON = JSON.stringify(newEndDates);
    const data = {
      ids: idsJSON,
      applyDateRow: applyDateRowJSON,
      startDates: newStartDatesJSON,
      endDates: newEndDatesJSON,
      subpanelValues,
    };
    const url = 'api/VPlanningDetailsBase/EditMultipleVPlanningDetailsBaseItemsByPricingIds/';
    return AuthService.getInstance().CallBackendFunctionPut(url, data);
  }

  static EditPlanningDetailsItem(editedItem) {
    const url = 'api/VPlanningDetailsBase/PutVPlanningDetailsBase/';
    editedItem.EndDate = dateTimeHelper.toYYYYsMMsDD(editedItem.EndDate);
    editedItem.StartDate = dateTimeHelper.toYYYYsMMsDD(editedItem.StartDate);
    editedItem.NumberOfPlannedAssignments = parseInt(editedItem.NumberOfPlannedAssignments);

    return AuthService.getInstance().CallBackendFunctionPut(url, editedItem);
  }

  static UpdateMultiplePlanningDetailItemStatusByPricingIds(
    pricingIds,
    applyDateRow,
    newStatus,
    cancellationDecision,
    cancellationComment
  ) {
    const idsJSON = JSON.stringify(pricingIds);
    const applyDateRowJSON = JSON.stringify(applyDateRow);
    const data = {
      Ids: idsJSON,
      ApplyDateRow: applyDateRowJSON,
      NewStatus: newStatus,
      CancellationDecision: cancellationDecision ?? null,
      CancellationComment: cancellationComment ?? null,
    };
    return AuthService.getInstance().CallBackendFunctionPut(
      'api/VPlanningDetailsBase/UpdateMultiplePlanningDetailItemStatusByPricingIds/',
      data
    );
  }

  static AddNewPlanningDetailItemsByPricingIds(pricingIds, numberOfNewItems) {
    const data = {
      Ids: JSON.stringify(pricingIds),
      NumberOfNewItems: numberOfNewItems.toString(),
    };

    return AuthService.getInstance().CallBackendFunctionPut(
      'api/VPlanningDetailsBase/AddNewPlanningDetailItemsByPricingIds/',
      data
    );
  }

  static UpdateContractedAssignmentsOverride(item) {
    const data = PlanningDetailHelper.BuildOverrideRequestData(item);
    return AuthService.getInstance().CallBackendFunctionPut('api/PlanningDetail/UpdateContractedOverride', data);
  }

  static ResetContractedAssignmentsOverride(item) {
    const data = { Ids: JSON.stringify(item.PlanningDetailIds) };
    return AuthService.getInstance().CallBackendFunctionPut('api/PlanningDetail/ResetContractedOverride', data);
  }

  static UnlockDates(item) {
    const data = { Ids: JSON.stringify(item.PlanningDetailIds) };
    return AuthService.getInstance().CallBackendFunctionPut('api/PlanningDetail/UnlockDates', data);
  }

  static LockDates(item) {
    const data = { Ids: JSON.stringify(item.PlanningDetailIds) };
    return AuthService.getInstance().CallBackendFunctionPut('api/PlanningDetail/LockDates', data);
  }

  static async GetRolloverSummaryByPricingId(pricingId) {
    const url = `api/PlanningDetail/GetRolloverSummaryByPricingId?pricingId=${pricingId}`;
    return AuthService.getInstance().CallBackendFunction(url);
  }

  static Rollover(item) {
    const targetItems = item.Items.filter((i) => i.PlanningDetailId !== item.SourcePlanningDetailId);
    const data = {
      Ids: JSON.stringify(item.PlanningDetailIds),
      SourcePlanningDetailId: item.SourcePlanningDetailId,
      DistributionType: item.DistributionType,
      Volume: item.DistributionType === 'even' && parseInt(item.Volume) > 0 ? parseInt(item.Volume) : null,
      Items:
        item.DistributionType === 'perItem'
          ? JSON.stringify(
              targetItems.map((i) => ({ PlanningDetailId: i.PlanningDetailId, Value: parseInt(i.rolloverValue) || 0 }))
            )
          : null,
    };
    return AuthService.getInstance().CallBackendFunctionPut('api/PlanningDetail/Rollover', data);
  }

  static ConvertDBPlanningDetailsItems(PlanningDetailsItems) {
    const convertedPlanningDetailsItems = [];

    PlanningDetailsItems.forEach((item) => {
      const newItem = new PlanningDetailsItem(item);
      convertedPlanningDetailsItems.push(newItem);
    });

    return convertedPlanningDetailsItems;
  }

  static ConvertDBPlanningDetailsForReportingItems(PlanningDetailsForReportingItems) {
    const convertedPlanningDetailsForReportingItems = [];

    PlanningDetailsForReportingItems.forEach((item) => {
      const newItem = new PlanningDetailsForReportingItem(item);
      convertedPlanningDetailsForReportingItems.push(newItem);
    });

    return convertedPlanningDetailsForReportingItems;
  }
}

export default PlanningDetailsItemService;
